// @greenlight-spec: 28-it-dashboard-ui.md
import type { StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import type { FeedbackStatus } from '@greenlight/shared';

// Feedback status → the shared StatusBadge vocabulary. Unread is the attention
// state (amber `pending`), read is settled (green `granted`), archived and sent
// are muted (`inactive`) — sent is a record of a hand-off, not something asking
// to be looked at. Color always pairs with the status label (docs/19).
export function statusVariant(status: FeedbackStatus): StatusBadgeVariant {
  switch (status) {
    case 'unread':
      return 'pending';
    case 'read':
      return 'granted';
    case 'archived':
    case 'sent':
      return 'inactive';
  }
}

/** A second badge when triage status is no longer `sent` but the hand-off remains. */
export function extraHandoffLabel(status: FeedbackStatus, sentAt: string | null): 'sent' | null {
  return sentAt !== null && status !== 'sent' ? 'sent' : null;
}

/** A list row: one item, or the newest of several items sharing a fingerprint. */
export type FeedbackRow<T> = T & { readonly groupIds: readonly string[] };

const fingerprintOf = (context: Record<string, unknown>): string | undefined => {
  const fingerprint = context['fingerprint'];
  return typeof fingerprint === 'string' && fingerprint !== '' ? fingerprint : undefined;
};

/**
 * Collapses items that carry the same `context.fingerprint` into one row, kept
 * at the position of the first (newest) of them, with every member's id so a
 * selection covers the whole group. Items without a fingerprint stay as they are.
 */
export function collapseByFingerprint<
  T extends { readonly id: string; readonly context: Record<string, unknown> },
>(items: readonly T[]): FeedbackRow<T>[] {
  const rows: FeedbackRow<T>[] = [];
  const byFingerprint = new Map<string, string[]>();
  for (const item of items) {
    const fingerprint = fingerprintOf(item.context);
    const group = fingerprint === undefined ? undefined : byFingerprint.get(fingerprint);
    if (group) {
      group.push(item.id);
      continue;
    }
    const ids = [item.id];
    if (fingerprint !== undefined) byFingerprint.set(fingerprint, ids);
    rows.push({ ...item, groupIds: ids });
  }
  return rows;
}

export const isObserverFiled = (context: Record<string, unknown>): boolean =>
  context['origin'] === 'observer';
